// src/lib/locale.ts
/**
 * Locale & Decimal Separator Support (T-25)
 * 
 * Provides locale-aware number formatting with decimal separator support.
 * Primary focus: Toggle between '.' and ',' decimal separators based on locale.
 * 
 * Acceptance Criteria: "3.5 -> 3,5 in comma locale"
 */

// Re-export from types for convenience
export type LocaleCode = 'en-GB' | 'en-US' | 'de-DE' | 'fr-FR' | 'es-ES';

/**
 * Configuration for a specific locale
 */
export interface LocaleConfig {
  /** Locale code */
  code: LocaleCode;
  /** Decimal separator character */
  decimalSeparator: '.' | ',';
  /** Display label for UI */
  label: string;
  /** Description for UI */
  description: string;
  /** Language name */
  language: string;
}

/**
 * Locale configurations mapping
 */
const LOCALE_CONFIGS: Record<LocaleCode, LocaleConfig> = {
  'en-GB': {
    code: 'en-GB',
    decimalSeparator: '.',
    label: 'English (UK)',
    description: 'Decimal point (3.5)',
    language: 'English'
  },
  'en-US': {
    code: 'en-US',
    decimalSeparator: '.',
    label: 'English (US)',
    description: 'Decimal point (3.5)',
    language: 'English'
  },
  'de-DE': {
    code: 'de-DE',
    decimalSeparator: ',',
    label: 'Deutsch',
    description: 'Decimal comma (3,5)',
    language: 'German'
  },
  'fr-FR': {
    code: 'fr-FR',
    decimalSeparator: ',',
    label: 'Français',
    description: 'Decimal comma (3,5)',
    language: 'French'
  },
  'es-ES': {
    code: 'es-ES',
    decimalSeparator: ',',
    label: 'Español',
    description: 'Decimal comma (3,5)',
    language: 'Spanish'
  }
};

/**
 * Get configuration for a locale
 * @param locale - Locale code
 * @returns Locale configuration
 */
export function getLocaleConfig(locale: LocaleCode): LocaleConfig {
  const config = LOCALE_CONFIGS[locale];
  if (!config) {
    console.warn(`Unknown locale: ${locale}, falling back to en-GB`);
    return LOCALE_CONFIGS['en-GB'];
  }
  return config;
}

/**
 * Get all available locale configurations
 * @returns Array of all locale configs
 */
export function getAllLocaleConfigs(): LocaleConfig[] {
  return Object.values(LOCALE_CONFIGS);
}

/**
 * Format a number according to locale decimal separator rules
 * T-25 CORE FUNCTION: This implements the main acceptance criteria
 * 
 * @param value - Number to format
 * @param locale - Target locale
 * @returns Formatted number string with appropriate decimal separator
 * 
 * @example
 * formatNumber(3.5, 'en-GB') // Returns "3.5"
 * formatNumber(3.5, 'fr-FR') // Returns "3,5" ← T-25 acceptance criteria
 * formatNumber(3.5, 'de-DE') // Returns "3,5"
 */
export function formatNumber(value: number, locale: LocaleCode): string {
  // Handle special cases
  if (!isFinite(value)) {
    return value.toString(); // NaN, Infinity, -Infinity
  }

  if (value === 0) {
    return '0';
  }

  // Get locale configuration
  const config = getLocaleConfig(locale);
  
  // Convert to string using JavaScript's default (always uses '.')
  const str = value.toString();
  
  // Replace decimal separator if needed
  if (config.decimalSeparator === ',') {
    return str.replace('.', ',');
  }
  
  return str;
}

/**
 * Parse a locale-formatted number string back to a JavaScript number
 * Handles both '.' and ',' decimal separators based on locale
 * 
 * @param input - Number string to parse
 * @param locale - Source locale for parsing rules
 * @returns Parsed number
 * @throws Error if the input is not a valid number for the locale
 * 
 * @example
 * parseNumber("3.5", 'en-GB') // Returns 3.5
 * parseNumber("3,5", 'fr-FR') // Returns 3.5
 * parseNumber("3,5", 'en-GB') // Throws error (invalid for dot locale)
 */
export function parseNumber(input: string, locale: LocaleCode): number {
  if (!input || typeof input !== 'string') {
    throw new Error(`Invalid input for parseNumber: ${input}`);
  }

  const trimmed = input.trim();
  if (trimmed === '') {
    throw new Error('Empty string cannot be parsed as number');
  }

  const config = getLocaleConfig(locale);
  
  // Normalize the input based on locale
  let normalized: string;
  
  if (config.decimalSeparator === ',') {
    // For comma locales, replace comma with dot for parsing
    // But first validate that the input uses comma (not dot)
    if (trimmed.includes('.') && trimmed.includes(',')) {
      throw new Error(`Ambiguous number format: ${input} (contains both . and ,)`);
    }
    
    normalized = trimmed.replace(',', '.');
  } else {
    // For dot locales, input should use dot
    // Reject comma usage in dot locales for strict validation
    if (trimmed.includes(',')) {
      throw new Error(`Invalid decimal separator for ${locale}: ${input} (expected .)`);
    }
    
    normalized = trimmed;
  }
  
  // Parse the normalized string
  const parsed = parseFloat(normalized);
  
  if (isNaN(parsed)) {
    throw new Error(`Invalid number format for locale ${locale}: ${input}`);
  }
  
  return parsed;
}

/**
 * Flexible number parsing that accepts both separators regardless of locale
 * Useful for user input where we want to be forgiving
 * 
 * @param input - Number string to parse
 * @returns Parsed number or null if invalid
 * 
 * @example
 * parseNumberFlexible("3.5") // Returns 3.5
 * parseNumberFlexible("3,5") // Returns 3.5
 * parseNumberFlexible("3.5.5") // Returns null (invalid)
 */
export function parseNumberFlexible(input: string): number | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const trimmed = input.trim();
  if (trimmed === '') {
    return null;
  }

  // If it contains both . and ,, it's ambiguous
  if (trimmed.includes('.') && trimmed.includes(',')) {
    return null;
  }

  // Try parsing by replacing comma with dot
  const normalized = trimmed.replace(',', '.');
  const parsed = parseFloat(normalized);
  
  return isNaN(parsed) ? null : parsed;
}

/**
 * Check if a locale uses comma as decimal separator
 * @param locale - Locale to check
 * @returns True if locale uses comma, false if it uses dot
 */
export function usesCommaDecimal(locale: LocaleCode): boolean {
  return getLocaleConfig(locale).decimalSeparator === ',';
}

/**
 * Format a number for display in input fields, considering locale
 * This is useful for form inputs where we want to show the number
 * in the user's expected format
 * 
 * @param value - Number to format
 * @param locale - User's locale
 * @returns Formatted string suitable for input fields
 */
export function formatNumberForInput(value: number | null | undefined, locale: LocaleCode): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  
  return formatNumber(value, locale);
}

/**
 * Convert a formatted number string from one locale to another
 * Useful for converting between display formats
 * 
 * @param input - Number string in source locale format
 * @param fromLocale - Source locale
 * @param toLocale - Target locale
 * @returns Number string in target locale format
 * @throws Error if input is invalid for source locale
 */
export function convertNumberFormat(input: string, fromLocale: LocaleCode, toLocale: LocaleCode): string {
  const parsed = parseNumber(input, fromLocale);
  return formatNumber(parsed, toLocale);
}

/**
 * Validate that a number string is correctly formatted for a locale
 * @param input - Number string to validate
 * @param locale - Expected locale
 * @returns True if valid, false if invalid
 */
export function isValidNumberFormat(input: string, locale: LocaleCode): boolean {
  try {
    parseNumber(input, locale);
    return true;
  } catch {
    return false;
  }
}

// Export for convenience
export { LOCALE_CONFIGS };

/**
 * Enhanced format number with precision control for tick labels
 * Combines locale support with decimal precision requirements from T-10
 * 
 * @param value - Number to format
 * @param locale - Target locale
 * @param maxDecimals - Maximum decimal places (default: 4)
 * @returns Formatted number string with locale decimal separator
 */
export function formatNumberWithPrecision(
  value: number, 
  locale: LocaleCode, 
  maxDecimals: number = 4
): string {
  // Handle special cases
  if (!isFinite(value)) {
    return value.toString();
  }

  if (Math.abs(value) < 1e-10) {
    return '0';
  }

  // Round to avoid floating point precision issues
  const rounded = Math.round(value * Math.pow(10, 10)) / Math.pow(10, 10);
  
  // Determine required decimal places
  let decimalPlaces = 0;
  
  // Check if it's effectively an integer
  if (Math.abs(rounded - Math.round(rounded)) < 1e-10) {
    decimalPlaces = 0;
  } else {
    // For non-integers, determine the minimum decimal places needed
    const fractionalPart = Math.abs(rounded - Math.floor(Math.abs(rounded)));
    
    // Convert to string to analyze decimal places needed
    const fractionalStr = fractionalPart.toFixed(10);
    
    // Count meaningful decimal places (ignore trailing zeros)
    let significantDecimals = 0;
    for (let i = 2; i < fractionalStr.length; i++) {
      if (fractionalStr[i] !== '0') {
        significantDecimals = i - 1;
      }
    }
    
    // Ensure at least 1 decimal place for non-integers, max as specified
    decimalPlaces = Math.max(1, Math.min(maxDecimals, significantDecimals));
    
    // Special cases for common decimal values
    const commonDecimals = [0.25, 0.5, 0.75, 0.125, 0.375, 0.625, 0.875];
    for (const common of commonDecimals) {
      if (Math.abs(fractionalPart - common) < 1e-10) {
        const commonDecimalPlaces = common.toString().split('.')[1]?.length || 1;
        decimalPlaces = Math.max(decimalPlaces, commonDecimalPlaces);
        break;
      }
    }
  }
  
  // Format with determined precision
  const formatted = rounded.toFixed(decimalPlaces);
  
  // Remove trailing zeros and unnecessary decimal points
  const cleaned = formatted.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  
  // Apply locale decimal separator
  const config = getLocaleConfig(locale);
  if (config.decimalSeparator === ',') {
    return cleaned.replace('.', ',');
  }
  
  return cleaned;
}